import React, { useCallback, useContext, useState } from 'react';
import FileDownloadContext from './FileDownload.context';
import { DOWNLOAD_BANNER_STATUS } from 'models/Download';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import utils, {
  getCanDownloadNode,
  getDownloadName,
  getIsNodeFolder,
  sortDataRoomNodes,
} from 'utils';
import {
  DownloadedSuccessfullyEventData,
  DownloadFailedEventData,
} from 'services/socketService/socketEventData';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import downloadService from 'services/downloadService';
import DownloadModal from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DownloadModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomSelectableNode } from 'router/subrouters/DataRoom/components/FileCheckSelector/types';
import {
  getIsNodeChecked,
  getIsNodeMixed,
} from 'router/subrouters/DataRoom/providers/EditShareableLink/utils';
import DownloadStatusBanner from 'components/DownloadStatusBanner';
import useDownloadBanner from 'hooks/useDownloadBanner';

export const prepareNodesForDownloadFileSelector = (
  nodes: DataRoomNode[],
  fileIds: string[] = [],
): DataRoomSelectableNode[] =>
  nodes.map((node) => {
    const disabled = !getCanDownloadNode(node);
    const isMixed = getIsNodeMixed(node, fileIds, true);
    const checked = !disabled && getIsNodeChecked(node, fileIds, true);

    return {
      ...node,
      isMixed,
      checked,
      disabled,
    };
  });

export const getSelectedNodes = (
  selectedNodes: DataRoomNode[],
  allNodes: DataRoomNode[],
): string[] =>
  (selectedNodes?.length ? selectedNodes : allNodes).flatMap((node) =>
    getIsNodeFolder(node) ? node.childrenIds : [node._id],
  );

const FileDownload: React.FC = (props) => {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [initiallyCheckedNodes, setInitiallyCheckedNodes] = useState<string[]>(
    [],
  );

  const [nodes, setNodes] = useState([]);
  const [dataRoomId, setDataRoomId] = useState<string>();
  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);

  const { downloadStatus, downloadBannerRef, showDownloadBanner } =
    useDownloadBanner();

  const prepareInitialModalNodes = useCallback(
    (selectedFileIds: string[], nodes: DataRoomNode[]) => {
      const sortedNodes = sortDataRoomNodes(nodes);
      const pNodes = prepareNodesForDownloadFileSelector(
        sortedNodes,
        selectedFileIds,
      );

      setInitiallyCheckedNodes(selectedFileIds);
      setNodes(pNodes);
    },
    [],
  );

  const openDownloadModal = useCallback(
    async (
      dataRoomId: string,
      selectedNodes: DataRoomNode[] = [],
      baseLevelNodes: DataRoomNode[] = [],
    ) => {
      setDataRoomId(dataRoomId);

      modal?.open();

      if (baseLevelNodes?.length) {
        prepareInitialModalNodes(
          getSelectedNodes(selectedNodes, baseLevelNodes),
          baseLevelNodes,
        );

        return;
      }

      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.nodes.getChildren(dataRoomId);

        prepareInitialModalNodes(getSelectedNodes(selectedNodes, data), data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [modal, popServerError, prepareInitialModalNodes],
  );

  const onCloseDownloadModal = useCallback(() => {
    setDataRoomId(undefined);
    setNodes([]);
  }, []);

  const downloadBulk = useCallback(
    async (dataRoomNodes: DataRoomNode[]) => {
      const name = getDownloadName();

      try {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.PREPARING);
        await dataRoomApi.nodes.bulkDownload(
          dataRoomId,
          dataRoomNodes.map(({ _id }) => _id),
          name,
          downloadService.downloadUniqueId,
        );
      } catch {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
      } finally {
        modal?.close();
        onCloseDownloadModal();
      }
    },
    [modal, dataRoomId, onCloseDownloadModal, showDownloadBanner],
  );

  const downloadSuccessfully = useCallback(
    ({ url, type, uniqueId }: DownloadedSuccessfullyEventData) => {
      if (uniqueId !== downloadService.downloadUniqueId) return;

      if (url) {
        utils.downloadUrl({ type, href: url });
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.SUCCESS);
      } else {
        showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
      }
    },
    [showDownloadBanner],
  );

  const downloadFailed = useCallback(
    ({ uniqueId }: DownloadFailedEventData) => {
      if (uniqueId !== downloadService.downloadUniqueId) return;

      showDownloadBanner(DOWNLOAD_BANNER_STATUS.ERROR);
    },
    [showDownloadBanner],
  );

  useSocketEvent(LISTEN_EVENTS.DOWNLOAD_SUCCESSFULLY, downloadSuccessfully);
  useSocketEvent(LISTEN_EVENTS.DOWNLOAD_FAILED, downloadFailed);

  return (
    <FileDownloadContext.Provider
      value={{
        showDownloadBanner,
        openDownloadModal,
      }}
    >
      {children}

      <DownloadStatusBanner
        ref={downloadBannerRef}
        variant={downloadStatus}
        onClose={() => showDownloadBanner(null)}
      />

      <DownloadModal
        nodes={nodes}
        isLoading={isLoading}
        initiallyCheckedNodes={initiallyCheckedNodes}
        ref={modalRef}
        onDownload={downloadBulk}
        onClose={onCloseDownloadModal}
        getChildren={(id: string) =>
          dataRoomApi.nodes.getChildren(dataRoomId, id)
        }
      />
    </FileDownloadContext.Provider>
  );
};

export default FileDownload;
